import React, { useCallback, useRef, useState } from 'react';
import Tooltip from '../Tooltip';
import { FormattedMessage } from 'react-intl';
import { switchProfile } from '../../auth/auth';
import useUserStore from '../../store/user';
import { useCanSwitchProfile } from '../../hooks/useAllAuthProfiles';
import { useUserProfiles } from '../../hooks/useUserProfiles';
import {
  IconExternalLink,
  IconLogout,
  IconRefresh,
  IconSettings,
  IconUser,
} from '../../icons';
import { Projection, projectionsWithLabels } from '../../helpers/math_helpers';
import useOrganizations from '../../hooks/useOrganizations';
import { Locale, localeNames, useLocale } from '../../store/locale';
import Button from '@/components/core/Button';
import ToggleButtonGroup from '@/components/core/ToggleButtonGroup';
import useClickOutside from '@/hooks/useClickOutside';
import IconButton from '@/components/core/IconButton';

const locales = Object.keys(localeNames).sort((a, b) =>
  a.localeCompare(b, 'sv'),
) as Locale[];

const languageOptions = locales.map((l) => ({
  value: l,
  label: localeNames[l],
}));

const projectionOptions = Object.values(projectionsWithLabels).map((proj) => ({
  value: proj.projection,
  label: proj.label,
}));

const UserMenu: React.FC<{ onLogout: () => void; textColor: string }> = ({
  onLogout,
  textColor,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { lookup: orgs } = useOrganizations();
  const name = useUserStore((s) => s.name);
  const email = useUserStore((s) => s.email);
  const tenantCode = useUserStore((s) => s.tenantCode);
  const role = useUserStore((s) => s.role);
  const organization = useUserStore((s) => s.organization);
  const {
    profile: { data: profile },
    setProfile: { mutate: setProfile },
  } = useUserProfiles();
  const canSwitchProfile = useCanSwitchProfile();

  const { setLocale } = useLocale();

  useClickOutside([containerRef], !open, () => setOpen(false));

  const onProjectionChange = useCallback(
    (value: string) => {
      if (!profile || !projectionsWithLabels[value as Projection]) {
        return;
      }
      const updatedProfile: Partial<UserProfile> = {
        ...profile,
        projection: value as Projection,
      };
      delete updatedProfile['meta'];
      const updatedProfileNoMeta = updatedProfile as Omit<UserProfile, 'meta'>;
      setProfile({ profile: updatedProfileNoMeta });
    },
    [profile, setProfile],
  );

  const onLanguageChange = useCallback(
    (value: string) => {
      if (!profile || !locales.includes(value as Locale)) {
        return;
      }
      setLocale(value as Locale);
    },
    [profile, setLocale],
  );

  const org = tenantCode && orgs[tenantCode];

  return (
    <div
      className="relative"
      ref={containerRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setOpen(false);
        }
      }}
    >
      <Tooltip label={name || email}>
        <IconButton
          size="lg"
          variant="ghost"
          icon={<IconUser className={textColor} />}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((isOpen) => !isOpen)}
          className="hover:bg-white/50"
          round
        />
      </Tooltip>
      {open && (
        <div
          role="dialog"
          aria-labelledby="user-menu-heading"
          className="absolute right-0 top-full z-50 mt-2 flex w-96 flex-col gap-4 rounded-md border border-background-300 bg-background-50 p-4 text-text-900 shadow-4"
        >
          <div>
            <p
              id="user-menu-heading"
              className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700"
            >
              <FormattedMessage id="user_menu.user" defaultMessage="User" />
            </p>
            <p className="text-base font-semibold leading-6">{name}</p>
            <p className="text-base leading-6">{email}</p>
          </div>
          {org && (
            <div>
              <p className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700">
                <FormattedMessage
                  id="user_menu.organization"
                  defaultMessage="Organization"
                />
              </p>
              <p className="text-base font-semibold leading-6">{org.name}</p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700">
              <FormattedMessage id="user_menu.role" defaultMessage="Role" />
            </p>
            <p className="text-base font-semibold leading-6">{role}</p>
          </div>
          {organization && (
            <div>
              <p className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700">
                <FormattedMessage
                  id="user_menu.contractor"
                  defaultMessage="Contractor"
                />
              </p>
              <p className="text-base font-semibold leading-6">
                {organization}
              </p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700">
              <FormattedMessage id="language" defaultMessage="Language" />
            </p>
            {!profile && (
              <div className="h-8 w-full animate-pulse rounded bg-background-300" />
            )}
            {profile && (
              <ToggleButtonGroup
                id="user-menu-language"
                options={languageOptions}
                value={profile.language}
                onChange={onLanguageChange}
                className="grow"
              />
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700">
              <FormattedMessage
                id="coordinate_system"
                defaultMessage="Coordinate system"
              />
            </p>
            {!profile && (
              <div className="h-8 w-full animate-pulse rounded bg-background-300" />
            )}
            {profile && (
              <ToggleButtonGroup
                id="user-menu-projection"
                options={projectionOptions}
                value={profile.projection}
                onChange={onProjectionChange}
                className="grow"
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="w-full"
              startIcon={<IconSettings />}
              endIcon={<IconExternalLink />}
              href="https://selfservice.identity.allbin.se/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              <FormattedMessage
                id="user_menu.account_settings"
                defaultMessage="Account settings"
              />
              <span className="sr-only">
                <FormattedMessage
                  id="user_menu.opens_in_new_tab"
                  defaultMessage="(opens in a new tab)"
                />
              </span>
            </Button>
            <div className="flex gap-2">
              {canSwitchProfile && (
                <Button
                  variant="outline"
                  className="w-full"
                  startIcon={<IconRefresh />}
                  onClick={switchProfile}
                >
                  <FormattedMessage
                    id="user_menu.switch_profile"
                    defaultMessage="Switch profile"
                  />
                </Button>
              )}
              <Button
                className="w-full"
                startIcon={<IconLogout />}
                onClick={onLogout}
              >
                <FormattedMessage
                  id="user_menu.logout"
                  defaultMessage="Log out"
                />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
